<script setup lang="ts">
import { computed, onBeforeMount, watch } from 'vue';

import { getAppNameFromCredType } from '@/app/utils/nodeTypesUtils';
import type {
	ICredentialDataDecryptedObject,
	ICredentialType,
	INode,
	INodeProperties,
} from 'n8n-workflow';
import { isCommunityPackageName } from 'n8n-workflow';

import type { IUpdateInformation } from '@/Interface';
import CredentialModeSelector, { type CredentialModeOption } from './CredentialModeSelector.vue';
import EnterpriseEdition from '@/app/components/EnterpriseEdition.ee.vue';
import { useI18n, addCredentialTranslation } from '@n8n/i18n';
import { useTelemetry } from '@n8n/composables/useTelemetry';
import {
	BUILTIN_CREDENTIALS_DOCS_URL,
	DOCS_DOMAIN,
	END_USER_CREDENTIALS_DOCS_URL,
	EnterpriseEditionFeature,
	NEW_ASSISTANT_SESSION_MODAL,
} from '@/app/constants';
import type { PermissionsRecord } from '@n8n/permissions';
import { useCredentialsStore } from '../../credentials.store';
import { injectNDVStore } from '@/features/ndv/shared/ndv.store';
import { useRootStore } from '@n8n/stores/useRootStore';
import { useUIStore } from '@/app/stores/ui.store';
import Banner from '@/app/components/Banner.vue';
import CopyInput from '@/app/components/CopyInput.vue';
import CredentialInputs from './CredentialInputs.vue';
import TemplatedAuthSimpleView from './TemplatedAuthSimpleView.vue';
import {
	listPlaceholderTitles,
	parseHttpUrl,
	TEMPLATED_CUSTOM_AUTH_CREDENTIAL_TYPE,
} from '@/features/credentials/templatedAuth.utils';
import GoogleAuthButton from './GoogleAuthButton.vue';
import { useChatPanelStore } from '@/features/ai/assistant/chatPanel.store';
import { useAssistantStore } from '@/features/ai/assistant/assistant.store';
import type { InstanceAiCredentialHelpHandler } from '@/app/composables/useInstanceAiEditorCapability';
import { CREDENTIAL_EDIT_MODAL_KEY } from '../../credentials.constants';
import FreeAiCreditsCallout from '@/app/components/FreeAiCreditsCallout.vue';

import {
	N8nButton,
	N8nCallout,
	N8nInfoTip,
	N8nInlineAskAssistantButton,
	N8nLink,
	N8nText,
} from '@n8n/design-system';
import CredentialTypeSelector from './CredentialTypeSelector.vue';
import { useQuickConnect } from '../../quickConnect/composables/useQuickConnect';
import QuickConnectButton from '../../quickConnect/components/QuickConnectButton.vue';
import QuickConnectBanner from '../../quickConnect/components/QuickConnectBanner.vue';
import { injectWorkflowDocumentStore } from '@/app/stores/workflowDocument.store';
import { ProjectTypes } from '@/features/collaboration/projects/projects.types';
import type { ProjectType } from '@/features/collaboration/projects/projects.types';

type Props = {
	mode: string;
	credentialType: ICredentialType;
	credentialProperties: INodeProperties[];
	credentialData: ICredentialDataDecryptedObject;
	credentialId?: string;
	credentialPermissions: PermissionsRecord['credential'];
	parentTypes?: string[];
	showValidationWarning?: boolean;
	authError?: string;
	testedSuccessfully?: boolean;
	isOAuthType?: boolean;
	isOAuthConnected?: boolean;
	isRetesting?: boolean;
	requiredPropertiesFilled?: boolean;
	isManaged?: boolean;
	isPrivateCredentialsEnabled?: boolean;
	isResolvable?: boolean;
	connectedByMe?: boolean;
	/** Provider account of the caller's own connection, for end-user credentials. */
	connectedAccountIdentifier?: string;
	isNewCredential?: boolean;
	/** Type of the project a new credential will be created in. */
	newCredentialProjectType?: ProjectType;
	managedOauthAvailable?: boolean;
	useCustomOauth?: boolean;
	isQuickConnectMode?: boolean;
	contextNode?: INode | null;
	hideAskAssistant?: boolean;
	/** Instance AI credential setup-help behavior, supplied by whoever opened the
	 *  modal (the editor capability, or the credentials list). Absent → no Instance
	 *  AI help button. */
	instanceAiCredentialHelp?: InstanceAiCredentialHelpHandler;
};

const props = withDefaults(defineProps<Props>(), {
	parentTypes: () => [],
	credentialId: '',
	authError: '',
	showValidationWarning: false,
	credentialPermissions: () => ({}) as PermissionsRecord['credential'],
	connectedAccountIdentifier: undefined,
	instanceAiCredentialHelp: undefined,
});
const emit = defineEmits<{
	update: [value: IUpdateInformation];
	authTypeChanged: [value: CredentialModeOption];
	scrollToTop: [];
	retest: [];
	oauth: [];
	disconnect: [];
	quickConnect: [];
	claimed: [];
	'update:isResolvable': [value: boolean];
}>();

const credentialsStore = useCredentialsStore();
const ndvStore = injectNDVStore();
const rootStore = useRootStore();
const uiStore = useUIStore();
const workflowDocumentStore = injectWorkflowDocumentStore();
const assistantStore = useAssistantStore();
const chatPanelStore = useChatPanelStore();

const i18n = useI18n();
const telemetry = useTelemetry();
const { getQuickConnectOption } = useQuickConnect();

onBeforeMount(async () => {
	uiStore.activeCredentialType = props.credentialType.name;

	if (rootStore.defaultLocale === 'en') return;

	const key = `n8n-nodes-base.credentials.${props.credentialType.name}`;

	if (i18n.exists(key)) return;

	const credTranslation = await credentialsStore.getCredentialTranslation(
		props.credentialType.name,
	);

	if (!credTranslation) return;

	addCredentialTranslation(
		{ [props.credentialType.name]: credTranslation },
		rootStore.defaultLocale,
	);
});

const serviceName = computed(() =>
	props.credentialType ? getAppNameFromCredType(props.credentialType.displayName) : '',
);
const appName = computed(
	() =>
		serviceName.value ||
		i18n.baseText('credentialEdit.credentialConfig.theServiceYouReConnectingTo'),
);
const credentialTypeName = computed(() => props.credentialType?.name);
const credentialOwnerName = computed(() =>
	credentialsStore.getCredentialOwnerNameById(`${props.credentialId}`),
);
// Team-owned credentials don't have a single human "owner", so naming the
// project ("Only My project can edit…") is misleading — point at the edit
// permission instead. Personal/shared credentials keep naming the owner.
const isHomeTeamProject = computed(
	() =>
		credentialsStore.getCredentialById(`${props.credentialId}`)?.homeProject?.type ===
		ProjectTypes.Team,
);
const documentationUrl = computed(() => {
	const type = props.credentialType;
	const activeNode = ndvStore.value.activeNode;
	const isCommunityNode = activeNode ? isCommunityPackageName(activeNode.type) : false;

	const docUrl = type?.documentationUrl;

	if (!docUrl) {
		return '';
	}

	let url: URL;
	if (docUrl.startsWith('https://') || docUrl.startsWith('http://')) {
		url = new URL(docUrl);
		if (url.hostname !== DOCS_DOMAIN) return docUrl;
	} else {
		// Don't show documentation link for community nodes if the URL is not an absolute path
		if (isCommunityNode) return '';
		else url = new URL(`${BUILTIN_CREDENTIALS_DOCS_URL}${docUrl}/`);
	}

	if (url.hostname === DOCS_DOMAIN) {
		url.searchParams.set('utm_source', 'n8n_app');
		url.searchParams.set('utm_medium', 'credential_settings');
		url.searchParams.set('utm_campaign', 'create_new_credentials_modal');
	}

	return url.href;
});

const isGoogleOAuthType = computed(
	() =>
		credentialTypeName.value === 'googleOAuth2Api' || props.parentTypes.includes('googleOAuth2Api'),
);

const oAuthCallbackUrl = computed(() => {
	const oauthType =
		credentialTypeName.value === 'oAuth2Api' || props.parentTypes.includes('oAuth2Api')
			? 'oauth2'
			: 'oauth1';
	return rootStore.OAuthCallbackUrls[oauthType as keyof {}];
});

const showOAuthSuccessBanner = computed(() => {
	return (
		props.isOAuthType &&
		props.requiredPropertiesFilled &&
		props.isOAuthConnected &&
		!props.authError
	);
});

const showOAuthNotConnectedBanner = computed(() => {
	return (
		props.isOAuthType &&
		props.requiredPropertiesFilled &&
		!props.isOAuthConnected &&
		!props.authError
	);
});

const isConnectedOAuth = computed(
	() => !!props.isOAuthType && !!props.requiredPropertiesFilled && !!props.isOAuthConnected,
);

// "Stale" = connected but the last credential test failed (e.g. token revoked or
// expired). In this state we promote Switch account over the plain Retry button.
const isStale = computed(() => isConnectedOAuth.value && !!props.authError);

// The connected account label — always the provider account the token belongs to,
// never the n8n account. For end-user creds it comes from the caller's own per-user
// connection, for fixed creds from the token stored on the credential. Many
// providers return no identity at all (Gmail asks for no identity scope), so an
// absent value is normal and falls back to a generic "Account connected" message.
const connectedAccountName = computed<string | undefined>(() => {
	if (props.isResolvable) {
		return props.connectedAccountIdentifier;
	}
	const identifier = props.credentialData?.accountIdentifier;
	return typeof identifier === 'string' && identifier ? identifier : undefined;
});

const connectedBannerMessage = computed(() =>
	connectedAccountName.value
		? i18n.baseText('credentialEdit.credentialConfig.connectedAs', {
				interpolate: { account: connectedAccountName.value },
			})
		: i18n.baseText('credentialEdit.credentialConfig.accountConnected'),
);

const isMissingCredentials = computed(() => props.credentialType === null);

const isNewCredential = computed(() => props.mode === 'new' && !props.credentialId);

const isAskAssistantAvailable = computed(
	() =>
		!props.hideAskAssistant &&
		documentationUrl.value &&
		documentationUrl.value.includes(DOCS_DOMAIN) &&
		props.credentialProperties.length &&
		props.credentialPermissions.update &&
		!(props.isOAuthType && props.requiredPropertiesFilled) &&
		assistantStore.isAssistantEnabled,
);

const assistantAlreadyAsked = computed<boolean>(() => {
	return assistantStore.isCredTypeActive(props.credentialType);
});

const canCreate = computed(() => isNewCredential.value && !!props.credentialPermissions.create);

const canEdit = computed(() => {
	return !isNewCredential.value && !!props.credentialPermissions.update;
});

const canWrite = computed(() => {
	return canCreate.value || canEdit.value;
});

// Switching a credential's type in either direction requires BOTH edit access
// and the createEndUser permission — the change edits the credential and affects
// every user's own connection, not just the caller's.
const canSelectEndUserType = computed(
	() => canWrite.value && !!props.credentialPermissions.createEndUser,
);

// Only in team projects; an existing end-user credential keeps the selector
// so it can be switched back to fixed.
const isEndUserTypeAvailable = computed(() => {
	if (props.isResolvable) return true;
	if (props.isNewCredential) return props.newCredentialProjectType === ProjectTypes.Team;
	return isHomeTeamProject.value;
});

// Connecting an existing private credential only needs the `connect` capability
// (no edit rights); shared/static credentials store the token on the shared
// credential itself, so connecting them follows the write permission.
const canConnect = computed(() => {
	if (!isNewCredential.value && props.isResolvable) {
		return !!props.credentialPermissions.connect;
	}
	return canWrite.value;
});

// When Instance AI is available it supersedes the legacy assistant for setup
// help. It guides any credential type, so it doesn't require an n8n-docs URL —
// otherwise the same UX gates apply (configurable properties, write access, not
// an already-connected OAuth credential).
const isInstanceAiCredentialHelpAvailable = computed(
	() =>
		!props.hideAskAssistant &&
		!!props.instanceAiCredentialHelp &&
		!!props.credentialProperties.length &&
		canWrite.value &&
		!(props.isOAuthType && props.requiredPropertiesFilled),
);

const activeNode = computed(() => ndvStore.value.activeNode);

const quickConnectOption = computed(() => {
	if (!activeNode.value) return undefined;
	return getQuickConnectOption(props.credentialType.name, activeNode.value.type);
});

const quickConnectAvailable = computed(() => !!quickConnectOption.value);

const quickConnectBannerText = computed(() => quickConnectOption.value?.text ?? '');

const isManagedOAuth = computed(
	() => props.isOAuthType && props.managedOauthAvailable && !props.useCustomOauth,
);

function onDataChange(event: IUpdateInformation): void {
	emit('update', event);
}

// Templated Custom Auth replaces the raw field set with its own pane: a
// guided form (one input per template {{marker}}) with an in-place
// "Edit setup" state for the machinery behind it.
const isTemplatedAuthType = computed(
	() => props.credentialType?.name === TEMPLATED_CUSTOM_AUTH_CREDENTIAL_TYPE,
);

// The templated auth probe only proves the service accepted the request —
// some services answer 2xx regardless of the key — so the green banner
// states that instead of claiming the connection was verified.
const testSuccessMessage = computed(() =>
	isTemplatedAuthType.value
		? i18n.baseText('credentialEdit.credentialConfig.authProbeAccepted')
		: i18n.baseText('credentialEdit.credentialConfig.connectionTestedSuccessfully'),
);

function onDocumentationUrlClick(): void {
	telemetry.track('User clicked credential modal docs link', {
		docs_link: documentationUrl.value,
		credential_type: credentialTypeName.value,
		source: 'modal',
		workflow_id: workflowDocumentStore.value.workflowId,
	});
}

function onAuthTypeChange(value: CredentialModeOption): void {
	emit('authTypeChanged', value);
}

// Instance AI credential setup help: run the host's behavior, then close the
// modal + NDV only if it asks us to. A new-tab hand-off (editor, credentials
// list) keeps them open so the user can finish the form; an in-thread append
// (artifact) closes them so the conversation comes into view.
async function onInstanceAiCredentialHelpClick() {
	// A recipe-created credential arrives pre-filled: the guided-form labels
	// steer the help thread to where-to-find guidance instead of setup steps,
	// and the recipe's key page lets it link the exact URL the recipe research
	// already verified.
	const placeholderTitles = isTemplatedAuthType.value
		? listPlaceholderTitles(props.credentialData)
		: [];
	const recipeDocsUrl = isTemplatedAuthType.value
		? parseHttpUrl(props.credentialData.docsUrl)
		: undefined;
	const shouldCloseModal = await props.instanceAiCredentialHelp?.({
		credentialType: props.credentialType.name,
		displayName: props.credentialType.displayName,
		nodeName: activeNode.value?.name,
		nodeType: activeNode.value?.type,
		id: props.credentialId || undefined,
		...(placeholderTitles.length ? { placeholderTitles } : {}),
		...(recipeDocsUrl ? { docsUrl: recipeDocsUrl } : {}),
		documentationUrl: documentationUrl.value || undefined,
		oauthRedirectUrl: props.isOAuthType ? oAuthCallbackUrl.value : undefined,
	});
	if (shouldCloseModal) {
		uiStore.closeModal(CREDENTIAL_EDIT_MODAL_KEY);
		ndvStore.value.unsetActiveNodeName();
	}
}

async function onAskAssistantClick() {
	const sessionInProgress = !assistantStore.isSessionEnded;
	if (sessionInProgress) {
		uiStore.openModalWithData({
			name: NEW_ASSISTANT_SESSION_MODAL,
			data: {
				context: {
					credHelp: {
						credType: props.credentialType,
					},
				},
			},
		});
		return;
	}
	await chatPanelStore.openWithCredHelp(props.credentialType);
}

watch(showOAuthSuccessBanner, (newValue, oldValue) => {
	if (newValue && !oldValue) {
		emit('scrollToTop');
	}
});
</script>

<template>
	<N8nCallout v-if="isManaged" theme="warning" icon="triangle-alert">
		{{ i18n.baseText('freeAi.credits.credentials.edit') }}
	</N8nCallout>
	<div v-else>
		<div :class="$style.config" data-test-id="node-credentials-config-container">
			<FreeAiCreditsCallout
				:credential-type-name="credentialType?.name"
				@claimed="$emit('claimed')"
			/>

			<CredentialModeSelector
				v-if="canWrite"
				:credential-type="credentialType"
				:use-custom-oauth="useCustomOauth"
				:show-managed-oauth-options="managedOauthAvailable"
				:quick-connect-available="quickConnectAvailable"
				:is-quick-connect-mode="isQuickConnectMode"
				:context-node="contextNode"
				@update:auth-type="onAuthTypeChange"
			/>

			<template v-if="isQuickConnectMode">
				<QuickConnectBanner
					v-if="quickConnectBannerText || quickConnectOption?.disclaimer"
					:text="quickConnectBannerText"
					:disclaimer="quickConnectOption?.disclaimer"
				/>
				<QuickConnectButton
					:service-name="serviceName"
					:credential-type-name="credentialType.name"
					data-test-id="quick-connect-modal-button"
					@click="$emit('quickConnect')"
				/>
			</template>

			<template v-else>
				<N8nCallout
					v-if="
						!isInstanceAiCredentialHelpAvailable &&
						documentationUrl &&
						credentialProperties.length &&
						!isManagedOAuth &&
						canWrite
					"
					:class="$style.docsCallout"
					theme="custom"
					iconless
				>
					{{ i18n.baseText('credentialEdit.credentialConfig.needHelpFillingOutTheseFields') }}
					<template #actions>
						<N8nLink :to="documentationUrl" size="small" @click="onDocumentationUrlClick">
							{{ i18n.baseText('credentialEdit.credentialConfig.openDocs') }}
						</N8nLink>
					</template>
				</N8nCallout>

				<template v-if="canWrite">
					<!-- Instance AI credential setup help (mimics the assistant button) -->
					<div
						v-if="isInstanceAiCredentialHelpAvailable"
						:class="$style.askAssistantButton"
						data-test-id="credential-edit-instance-ai-help-button"
					>
						<N8nInlineAskAssistantButton
							:label="i18n.baseText('instanceAi.askAiAssistant')"
							@click="onInstanceAiCredentialHelpClick"
						/>
						<span>
							{{
								i18n.baseText('credentialEdit.credentialConfig.assistantHelp.forSetupInstructions')
							}}
							<template
								v-if="
									documentationUrl && credentialProperties.length && !isManagedOAuth && canWrite
								"
							>
								{{ i18n.baseText('credentialEdit.credentialConfig.assistantHelp.orReadThe') }}
								<N8nLink :to="documentationUrl" size="small" @click="onDocumentationUrlClick">
									{{ i18n.baseText('credentialEdit.credentialConfig.assistantHelp.docs') }}
								</N8nLink>
							</template>
						</span>
					</div>
					<!-- Legacy assistant credential help — only while Instance AI is off -->
					<div
						v-else-if="isAskAssistantAvailable"
						:class="$style.askAssistantButton"
						data-test-id="credential-edit-ask-assistant-button"
					>
						<N8nInlineAskAssistantButton
							:asked="assistantAlreadyAsked"
							@click="onAskAssistantClick"
						/>
						<span>
							{{
								i18n.baseText('credentialEdit.credentialConfig.assistantHelp.forSetupInstructions')
							}}
						</span>
					</div>
				</template>

				<Banner
					v-show="showValidationWarning"
					theme="danger"
					:message="
						i18n.baseText(
							`credentialEdit.credentialConfig.pleaseCheckTheErrorsBelow${
								credentialPermissions.update ? '' : '.sharee'
							}`,
							{ interpolate: { owner: credentialOwnerName } },
						)
					"
				/>

				<Banner
					v-if="authError && !showValidationWarning"
					theme="danger"
					:message="
						i18n.baseText(
							`credentialEdit.credentialConfig.couldntConnectWithTheseSettings${
								credentialPermissions.update ? '' : '.sharee'
							}`,
							{ interpolate: { owner: credentialOwnerName } },
						)
					"
					:details="authError"
					:button-label="i18n.baseText('credentialEdit.credentialConfig.retry')"
					button-loading-label="Retrying"
					:button-title="i18n.baseText('credentialEdit.credentialConfig.retryCredentialTest')"
					:button-loading="isRetesting"
					@click="$emit('retest')"
				>
					<!-- A stale connection (connected, but the last test failed) promotes
						 Switch account over the plain Retry button. -->
					<template v-if="isStale && canConnect" #button>
						<div :class="$style.bannerActions">
							<N8nButton
								size="small"
								:label="i18n.baseText('credentialEdit.credentialConfig.switchAccount')"
								data-test-id="oauth-stale-switch-account-button"
								@click="$emit('oauth')"
							/>
							<N8nButton
								variant="subtle"
								size="small"
								:class="$style.disconnectButton"
								:label="i18n.baseText('credentialEdit.credentialConfig.disconnect')"
								data-test-id="oauth-stale-disconnect-button"
								@click="$emit('disconnect')"
							/>
						</div>
					</template>
				</Banner>

				<!-- Type selection stays above the connection banners: the connect /
					 connected banner always renders below the selector, so it keeps a
					 stable position when the credential connects or the type changes. -->
				<CredentialTypeSelector
					v-if="
						isPrivateCredentialsEnabled &&
						// Only OAuth credentials can be dynamic for now, as they are the only ones with the managed authorize endpoint
						isOAuthType &&
						// Only users who can manage end-user credentials see the selector at all;
						// it's disabled for them when they lack edit access to the credential.
						!!credentialPermissions.createEndUser &&
						// End-user credentials are not available in personal projects.
						isEndUserTypeAvailable
					"
					:model-value="Boolean(isResolvable)"
					:disabled="!canSelectEndUserType"
					:info-tip="i18n.baseText('credentialEdit.credentialConfig.dynamicCredentials.infoTip')"
					@update:model-value="(val) => $emit('update:isResolvable', val)"
				/>

				<Banner
					v-show="showOAuthSuccessBanner && !showValidationWarning"
					theme="success"
					:message="connectedBannerMessage"
					data-test-id="oauth-connect-success-banner"
				>
					<template v-if="isResolvable" #subtitle>
						<N8nText size="small" color="text-light">
							{{
								i18n.baseText('credentialEdit.credentialConfig.endUserCredential.connectedSubtext')
							}}&nbsp;
						</N8nText>
						<N8nLink theme="text" underline :to="END_USER_CREDENTIALS_DOCS_URL" size="small">
							{{ i18n.baseText('generic.learnMore') }}
						</N8nLink>
					</template>
					<template v-if="canConnect" #button>
						<div :class="$style.bannerActions">
							<N8nButton
								variant="subtle"
								size="small"
								:label="i18n.baseText('credentialEdit.credentialConfig.switchAccount')"
								data-test-id="oauth-switch-account-button"
								@click="$emit('oauth')"
							/>
							<N8nButton
								variant="subtle"
								size="small"
								:class="$style.disconnectButton"
								:label="i18n.baseText('credentialEdit.credentialConfig.disconnect')"
								data-test-id="oauth-disconnect-button"
								@click="$emit('disconnect')"
							/>
						</div>
					</template>
				</Banner>

				<Banner
					v-show="testedSuccessfully && !showValidationWarning"
					theme="success"
					:message="testSuccessMessage"
					:button-label="i18n.baseText('credentialEdit.credentialConfig.retry')"
					:button-loading-label="i18n.baseText('credentialEdit.credentialConfig.retrying')"
					:button-title="i18n.baseText('credentialEdit.credentialConfig.retryCredentialTest')"
					:button-loading="isRetesting"
					data-test-id="credentials-config-container-test-success"
					@click="$emit('retest')"
				/>

				<Banner
					v-show="showOAuthNotConnectedBanner && !showValidationWarning"
					:theme="isResolvable ? 'info' : 'warning'"
					:message="
						isResolvable
							? i18n.baseText('credentialEdit.credentialConfig.accountNotConnected.endUser', {
									interpolate: { service: serviceName },
								})
							: i18n.baseText('credentialEdit.credentialConfig.accountNotConnected')
					"
					:button-label="i18n.baseText('credentialEdit.credentialConfig.connect')"
					:button-title="i18n.baseText('credentialEdit.credentialConfig.connectOAuth2Credential')"
					data-test-id="oauth-not-connected-banner"
					@click="$emit('oauth')"
				>
					<template v-if="isResolvable" #subtitle>
						<N8nText size="small" color="text-light">
							{{
								i18n.baseText('credentialEdit.credentialConfig.endUserCredential.connectSubtext')
							}}&nbsp;
						</N8nText>
						<N8nLink theme="text" underline :to="END_USER_CREDENTIALS_DOCS_URL" size="small">
							{{ i18n.baseText('generic.learnMore') }}
						</N8nLink>
					</template>
					<template v-if="isGoogleOAuthType" #button>
						<div v-if="canConnect" data-test-id="quick-connect-button">
							<GoogleAuthButton @click="$emit('oauth')" />
						</div>
					</template>
					<template v-else #button>
						<QuickConnectButton
							v-if="canConnect"
							size="small"
							:service-name="serviceName"
							:credential-type-name="credentialType.name"
							:label="i18n.baseText('credentialEdit.credentialConfig.connect')"
							data-test-id="quick-connect-button"
							@click="$emit('oauth')"
						/>
					</template>
				</Banner>

				<template v-if="canWrite">
					<CopyInput
						v-if="isOAuthType && !isManagedOAuth"
						:label="i18n.baseText('credentialEdit.credentialConfig.oAuthRedirectUrl')"
						:value="oAuthCallbackUrl"
						:copy-button-text="i18n.baseText('credentialEdit.credentialConfig.clickToCopy')"
						:hint="
							i18n.baseText('credentialEdit.credentialConfig.subtitle', {
								interpolate: { appName },
							})
						"
						:toast-title="
							i18n.baseText('credentialEdit.credentialConfig.redirectUrlCopiedToClipboard')
						"
						:redact-value="true"
					/>
				</template>
				<EnterpriseEdition v-else :features="[EnterpriseEditionFeature.Sharing]">
					<div>
						<N8nInfoTip :bold="false">
							{{
								isHomeTeamProject
									? i18n.baseText('credentialEdit.credentialEdit.info.sharee.team')
									: i18n.baseText('credentialEdit.credentialEdit.info.sharee', {
											interpolate: { credentialOwnerName },
										})
							}}
						</N8nInfoTip>
					</div>
				</EnterpriseEdition>

				<TemplatedAuthSimpleView
					v-if="credentialType && canWrite && isTemplatedAuthType"
					:credential-data="credentialData"
					@update="onDataChange"
				/>
				<CredentialInputs
					v-else-if="credentialType && canWrite"
					:credential-data="credentialData"
					:credential-properties="credentialProperties"
					:documentation-url="documentationUrl"
					:show-validation-warnings="showValidationWarning"
					@update="onDataChange"
				/>

				<N8nText v-if="isMissingCredentials" color="text-base" size="medium">
					{{ i18n.baseText('credentialEdit.credentialConfig.missingCredentialType') }}
				</N8nText>

				<EnterpriseEdition :features="[EnterpriseEditionFeature.ExternalSecrets]">
					<template #fallback>
						<N8nInfoTip class="mt-s">
							{{ i18n.baseText('credentialEdit.credentialConfig.externalSecrets') }}
							<N8nLink bold :to="i18n.baseText('settings.externalSecrets.docs')" size="small">
								{{ i18n.baseText('credentialEdit.credentialConfig.externalSecrets.moreInfo') }}
							</N8nLink>
						</N8nInfoTip>
					</template>
				</EnterpriseEdition>
			</template>
		</div>
	</div>
</template>

<style lang="scss" module>
.config {
	--notice--margin: 0;
	flex-grow: 1;

	> * + * {
		margin-bottom: var(--spacing--lg);
	}
}

.bannerActions {
	display: flex;
	align-items: center;
	gap: var(--spacing--2xs);
}

// Outline button tinted for the destructive "Disconnect" action so it reads as
// the primary action without the heaviness of a filled destructive button.
.disconnectButton {
	--button--color: var(--color--danger);
	--button--color--hover: var(--color--danger);
	--button--color--active: var(--color--danger);
	--button--border-color: var(--color--danger);
	--button--border-color--hover: var(--color--danger);
	--button--border-color--active: var(--color--danger);
}

.askAssistantButton {
	display: flex;
	align-items: center;

	> span {
		margin-left: var(--spacing--3xs);
		font-size: var(--font-size--sm);
	}
}

.docsCallout {
	// Match the neutral connect banner tint (was too dark in light mode).
	background-color: light-dark(var(--color--black-alpha-50), var(--color--white-alpha-100));
	border-color: light-dark(var(--color--black-alpha-100), var(--color--white-alpha-300));

	a {
		text-decoration: none;
	}
}
</style>
